import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useERDState } from '../ERDEditor/hooks/useERDState';
import { useClass } from '../../utils/ClassContext';
import RawEditor from '../ERDEditor/RawEditor';
import VisualEditor from '../ERDEditor/VisualEditor';
import ERDVisualization from '../ERDEditor/ERDVisualization';
import { activityApi } from '../../utils/api/DocumentEditorApi/ActivityApi';

/**
 * Embedded ERD Snippet Component
 *
 * Embeds the full ERD editor module in documents (like CodeSnippet embeds Monaco).
 * - Instructors: Can edit ERD, changes saved via main document Save button
 * - Students: Can interact and generate, but changes not saved
 *
 * Usage in markdown:
 * ```erd
 * // ERD text input (same format as ERD editor)
 * ```
 */

/** Format a saved-at Date as a human-readable relative string. */
function formatSavedAt(date) {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  // Fall back to locale time for older saves
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const ERDSnippet = ({
  children,
  className = '',
  isStudent = false,
  snippetId = null,
  documentId = null,
  classContext = null,
  erdState = null,  // Shared erdState from DocumentEditor (for feedback panel sync)
  environmentId = null,
  username = null,
  readOnly = false,
  onNotationChange = null,       // (newNotation: string) => void — instructors only; updates documentContent in parent
  onRequestFeedback = null,      // () => void — called when user clicks "Get AI Feedback"; switches right panel to AI Guide
  isEditable = false,            // true when info string contains :editable — shows Edit Diagram button
  isSavable = false,             // true when info string contains :savable — students can edit & work is persisted
  // Document-level requirements linked via :envname syntax (from PreviewPane requirementsMap)
  requirements: docRequirements = [],
  env = null
}) => {
  const { getClassContext } = useClass();
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'raw' (students locked to 'visual')
  const [editorOpen, setEditorOpen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // Undo/redo history — each entry is { inputText, svgContent }
  const historyRef = useRef({ items: [], index: -1 });
  const [, setHistoryTick] = useState(0); // bumped to trigger re-render on history change
  const isRestoringRef = useRef(false);              // true during undo/redo or initial DB restore
  // isSavable state
  const [erdSaveStatus, setErdSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [erdSavedAt, setErdSavedAt] = useState(null); // Date of last save (this session or restored)
  // Ticker to re-render relative timestamp every 30s
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!erdSavedAt) return;
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, [erdSavedAt]);
  // Block the initial auto-generate until we've checked for previous work
  const [loadingPreviousWork, setLoadingPreviousWork] = useState(isSavable && isStudent);
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsDirty, setRequirementsDirty] = useState(false);
  const [requirementsSaving, setRequirementsSaving] = useState(false);
  const savedRequirementsRef = useRef([]);

  // Parse initial ERD content from children (raw text format)
  const initialContent = typeof children === 'string' ? children : (children?.props?.children || '');

  // IMPORTANT: Always use local state for ERD notation (like CodeSnippet does)
  // Each ERD snippet manages its own notation independently from the document
  // The shared erdState from DocumentEditor is only used for feedback panel
  const localErdState = useERDState(
    classContext?.username || 'student',
    null,
    null,
    getClassContext,
    documentId,  // Pass documentId for activity tracking
    'embedded'   // Source is always 'embedded' for ERDSnippet
  );

  // Always use local state for notation, never shared state
  // This ensures each ERD snippet has its own independent notation from the document
  const activeErdState = localErdState;

  // Track if this snippet is focused/active
  const [isActive, setIsActive] = useState(false);

  // Keep a ref to erdState so the sync effect can access the latest version
  // without putting erdState in the deps array (which would cause the effect to
  // re-fire every DocumentEditor render, since erdState is a new object each time).
  const erdStateRef = useRef(erdState);
  useEffect(() => { erdStateRef.current = erdState; });

  // Sync local state to shared state for feedback panel
  // ONLY sync when this snippet is active (user is interacting with it)
  // This prevents conflicts when multiple ERD snippets exist in the same document
  useEffect(() => {
    const shared = erdStateRef.current;
    if (isActive && shared && activeErdState.relationships) {
      // Sync relationships list
      if (shared.setRelationships) {
        shared.setRelationships(activeErdState.relationships);
      }
      // Sync selected relationship
      if (shared.setSelectedRelationship && activeErdState.selectedRelationship) {
        shared.setSelectedRelationship(activeErdState.selectedRelationship);
      }
      // Sync ERD data for feedback generation
      if (shared.setErdData && activeErdState.erdData) {
        shared.setErdData(activeErdState.erdData);
      }
      // Sync input text for feedback
      if (shared.setInputText && activeErdState.inputText) {
        shared.setInputText(activeErdState.inputText);
      }
    }
  }, [isActive, activeErdState.relationships, activeErdState.selectedRelationship, activeErdState.erdData, activeErdState.inputText, snippetId]);

  // Mark this snippet as active when user interacts with it
  const handleSnippetClick = useCallback(() => {
    setIsActive(true);
  }, []);

  // Mark as active when ERD is generated or relationship is selected
  useEffect(() => {
    if (activeErdState.svgContent || activeErdState.selectedRelationship !== 'All') {
      setIsActive(true);
    }
  }, [activeErdState.svgContent, activeErdState.selectedRelationship]);

  // Track activity changes
  const lastActivityLogRef = useRef(null);
  const activityTimeoutRef = useRef(null);

  // Set initial content from document — runs only when initialContent changes.
  // IMPORTANT: activeErdState must NOT be in the dependency array. useERDState returns
  // a new object reference on every render, so including it would cause this effect to
  // re-fire after every VisualEditor change (which calls setInputText), resetting the
  // notation back to the original markdown content and breaking the visual editor.
  const setInputTextRef = useRef(activeErdState.setInputText);
  useEffect(() => { setInputTextRef.current = activeErdState.setInputText; });

  const generateERDRef = useRef(activeErdState.generateERD);
  useEffect(() => { generateERDRef.current = activeErdState.generateERD; });

  const isGeneratingRef = useRef(activeErdState.isGenerating);
  useEffect(() => { isGeneratingRef.current = activeErdState.isGenerating; });

  // Queue flag: set when a form change arrives while a generation is in-flight.
  const pendingRegenerateRef = useRef(false);
  // Set to true once the initial load check completes — guards against saving during mount.
  const initCompleteRef = useRef(false);

  // Auto-regenerate the diagram after any visual editor form change (add entity, etc.).
  // If a generation is already running, queue it and fire once the current one finishes.
  const handleVisualEditorSaveEvent = useCallback(() => {
    if (isGeneratingRef.current) {
      pendingRegenerateRef.current = true;
    } else {
      generateERDRef.current();
    }
  }, []);

  // Fire the queued regeneration as soon as isGenerating flips to false.
  useEffect(() => {
    if (!activeErdState.isGenerating && pendingRegenerateRef.current) {
      pendingRegenerateRef.current = false;
      generateERDRef.current();
    }
  }, [activeErdState.isGenerating]);

  // isSavable: load the student's previous work on mount.
  // If found, restore inputText + svgContent immediately so the diagram appears
  // without waiting for a re-generation API call.
  useEffect(() => {
    console.log('[ERD:savable] load effect — isSavable:', isSavable, 'isStudent:', isStudent, 'documentId:', documentId, 'snippetId:', snippetId);
    if (!isSavable || !isStudent || !documentId || !snippetId) {
      console.log('[ERD:savable] skipping load (missing props)');
      setLoadingPreviousWork(false);
      return;
    }
    const ctx = classContext || getClassContext();
    console.log('[ERD:savable] calling loadErdWork, ctx:', ctx);
    activityApi.loadErdWork(snippetId, documentId, ctx)
      .then(result => {
        console.log('[ERD:savable] loadErdWork result:', result);
        if (result?.success && result.inputText) {
          setInputTextRef.current(result.inputText);
          if (result.svgContent && activeErdState.setSvgContent) {
            isRestoringRef.current = true; // prevent history push for the initial restore
            activeErdState.setSvgContent(result.svgContent);
            hasAutoGeneratedRef.current = true;
            // Seed history so the user can undo back to this loaded state
            historyRef.current = {
              items: [{ inputText: result.inputText, svgContent: result.svgContent }],
              index: 0,
            };
            setHistoryTick(t => t + 1);
          }
          if (result.savedAt) {
            setErdSavedAt(new Date(result.savedAt));
          }
        }
      })
      .catch((err) => { console.warn('[ERD:savable] loadErdWork error:', err); })
      .finally(() => {
        console.log('[ERD:savable] load complete, setting initCompleteRef=true');
        initCompleteRef.current = true;
        setLoadingPreviousWork(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // isSavable: after each generation, persist work (once initialization is complete).
  useEffect(() => {
    if (!isSavable || !isStudent || !documentId || !snippetId) return;
    if (!activeErdState.svgContent) return;
    if (!initCompleteRef.current) {
      console.log('[ERD:savable] save skipped — initComplete not set yet');
      return;
    }
    const ctx = classContext || getClassContext();
    console.log('[ERD:savable] saving — documentId:', documentId, 'snippetId:', snippetId, 'ctx:', ctx);
    setErdSaveStatus('saving');
    activityApi.saveErdWork(documentId, snippetId, activeErdState.inputText, activeErdState.svgContent, ctx)
      .then(() => {
        console.log('[ERD:savable] save success');
        const now = new Date();
        setErdSavedAt(now);
        setErdSaveStatus('saved');
        setTimeout(() => setErdSaveStatus('idle'), 3000);
      })
      .catch((err) => { console.warn('[ERD:savable] save error:', err); setErdSaveStatus('idle'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeErdState.svgContent]);

  // Track each new generation in the undo/redo history stack.
  // Skipped during initial DB restore and during undo/redo operations (isRestoringRef).
  useEffect(() => {
    if (!activeErdState.svgContent) return;
    if (isRestoringRef.current) { isRestoringRef.current = false; return; }
    const entry = { inputText: activeErdState.inputText, svgContent: activeErdState.svgContent };
    const { items, index } = historyRef.current;
    const newItems = [...items.slice(0, index + 1), entry];
    historyRef.current = { items: newItems, index: newItems.length - 1 };
    setHistoryTick(t => t + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeErdState.svgContent]);

  // Track the content value we last initialized from, so the propagation effect below
  // can distinguish "initial load" from "user edit".
  const lastInitializedContentRef = useRef(null);

  useEffect(() => {
    if (initialContent && initialContent.trim()) {
      setInputTextRef.current(initialContent);
      lastInitializedContentRef.current = initialContent;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContent]);

  // Auto-generate the diagram as soon as initial content is loaded, so the
  // visualization is visible immediately without requiring a manual "Generate" click.
  // The flag ensures we only auto-generate once per mount, not on every keystroke.
  const hasAutoGeneratedRef = useRef(false);
  const { inputText: currentInputText } = activeErdState;
  useEffect(() => {
    if (hasAutoGeneratedRef.current) return;
    if (loadingPreviousWork) return; // wait until previous work is loaded (isSavable only)
    if (!currentInputText || !currentInputText.trim()) return;
    hasAutoGeneratedRef.current = true;
    generateERDRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInputText, loadingPreviousWork]);

  // Propagate notation changes up to DocumentEditor so instructors can save them.
  // Students: changes are intentionally ephemeral — they live in localErdState only
  // and disappear when the component unmounts (user leaves the document).
  useEffect(() => {
    if (isStudent) return;
    if (!onNotationChange) return;
    if (!currentInputText) return;
    // Skip if this is just echoing back the value we initialized with.
    if (currentInputText === lastInitializedContentRef.current) return;
    onNotationChange(currentInputText);
  }, [currentInputText, isStudent, onNotationChange]);

  // Fetch requirements when environmentId is available
  useEffect(() => {
    if (!environmentId || !classContext?.class_code) return;

    const fetchRequirements = async () => {
      setRequirementsLoading(true);
      try {
        const response = await fetch(`/api/requirements/${environmentId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Class-Code': classContext.class_code
          },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          const fetched = data.requirements || [];
          setRequirements(fetched);
          savedRequirementsRef.current = fetched;
        }
      } catch (err) {
        console.warn('[ERDSnippet] Failed to fetch requirements:', err);
      } finally {
        setRequirementsLoading(false);
      }
    };

    fetchRequirements();
  }, [environmentId, classContext?.class_code]);

  // Track whether requirements have been modified since last save
  useEffect(() => {
    if (requirementsLoading) return;
    const saved = savedRequirementsRef.current;
    const dirty = requirements.length !== saved.length ||
      requirements.some((r, i) => r !== saved[i]);
    setRequirementsDirty(dirty);
  }, [requirements, requirementsLoading]);

  // Save requirements to the backend
  const saveRequirements = async (reqs) => {
    if (!environmentId || !classContext?.class_code) return;
    setRequirementsSaving(true);
    try {
      const response = await fetch(`/api/requirements/${environmentId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Class-Code': classContext.class_code
        },
        credentials: 'include',
        body: JSON.stringify({
          version: `inline_${Date.now()}`,
          requirements: reqs.filter(r => r.trim() !== '')
        })
      });
      if (response.ok) {
        const cleaned = reqs.filter(r => r.trim() !== '');
        setRequirements(cleaned);
        savedRequirementsRef.current = cleaned;
      }
    } catch (err) {
      console.warn('[ERDSnippet] Save requirements failed:', err);
    } finally {
      setRequirementsSaving(false);
    }
  };

  // Log activity when ERD changes (debounced)
  const logERDActivity = useCallback((activityType, description, metadata = {}) => {
    if (!isStudent || !documentId || !classContext || !snippetId) return;

    // Clear previous timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Debounce activity logging (wait 2 seconds after last change)
    activityTimeoutRef.current = setTimeout(() => {
      const now = Date.now();
      const lastLog = lastActivityLogRef.current;

      // Don't log if we just logged the same activity within 5 seconds
      if (lastLog && now - lastLog < 5000) {
        return;
      }

      activityApi.logActivity(
        documentId,
        activityType,
        description,
        {
          metadata: {
            snippetId: snippetId,
            erdNotation: activeErdState.erdNotation,
            ...metadata
          }
        },
        classContext
      ).then(() => {
        lastActivityLogRef.current = now;
      }).catch(err => {
        console.warn('Failed to log ERD activity:', err);
      });
    }, 2000);
  }, [isStudent, documentId, classContext, snippetId, activeErdState.erdNotation]);

  // Track entity changes
  useEffect(() => {
    if (!activeErdState.entities || activeErdState.entities.length === 0) return;

    logERDActivity(
      'erd_entity_modified',
      `Modified ERD entities`,
      {
        entityCount: activeErdState.entities.length,
        entityNames: activeErdState.entities.map(e => e.name).join(', ')
      }
    );
  }, [activeErdState.entities, logERDActivity]);

  // Track relationship changes
  useEffect(() => {
    if (!activeErdState.relationships || activeErdState.relationships.length === 0) return;

    logERDActivity(
      'erd_relationship_modified',
      `Modified ERD relationships`,
      {
        relationshipCount: activeErdState.relationships.length
      }
    );
  }, [activeErdState.relationships, logERDActivity]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, []);

  // Handle Clear ERD button
  const handleClearConfirmed = () => {
    activeErdState.setInputText('');
    if (activeErdState.setSvgContent) activeErdState.setSvgContent('');
    if (onNotationChange && !isStudent) onNotationChange('');
    setShowClearConfirm(false);
  };

  // Undo: step back one history entry
  const handleUndo = useCallback(() => {
    const { items, index } = historyRef.current;
    if (index <= 0) return;
    const entry = items[index - 1];
    isRestoringRef.current = true;
    historyRef.current = { items, index: index - 1 };
    setHistoryTick(t => t + 1);
    setInputTextRef.current(entry.inputText);
    if (activeErdState.setSvgContent) activeErdState.setSvgContent(entry.svgContent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redo: step forward one history entry
  const handleRedo = useCallback(() => {
    const { items, index } = historyRef.current;
    if (index >= items.length - 1) return;
    const entry = items[index + 1];
    isRestoringRef.current = true;
    historyRef.current = { items, index: index + 1 };
    setHistoryTick(t => t + 1);
    setInputTextRef.current(entry.inputText);
    if (activeErdState.setSvgContent) activeErdState.setSvgContent(entry.svgContent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Generate ERD button
  const handleGenerate = async () => {
    try {
      await activeErdState.generateERD();
    } catch (error) {
      console.error('Error generating ERD:', error);
    }
  };

  // Export requirements as JSON
  const handleExportJSON = async () => {
    if (!environmentId || !classContext?.class_code) return;
    try {
      const response = await fetch(`/api/requirements/${environmentId}/export?format=json`, {
        headers: { 'Class-Code': classContext.class_code },
        credentials: 'include'
      });
      if (!response.ok) return;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requirements_${environmentId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[ERDSnippet] Export failed:', err);
    }
  };

  // Export full environment as JSON
  const handleExportFull = async () => {
    if (!environmentId || !classContext?.class_code) return;
    try {
      const response = await fetch(`/api/requirements/${environmentId}/export-full`, {
        headers: { 'Class-Code': classContext.class_code },
        credentials: 'include'
      });
      if (!response.ok) return;

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `environment_${environmentId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[ERDSnippet] Full export failed:', err);
    }
  };

  // Import requirements from a JSON file
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !environmentId || !classContext?.class_code) return;
    e.target.value = ''; // reset so same file can be re-selected

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const reqs = data.requirements || data;
      const importedRequirements = Array.isArray(reqs)
        ? reqs.map(r => (typeof r === 'string' ? r : r.description || ''))
        : [];

      if (importedRequirements.length === 0) {
        console.warn('[ERDSnippet] No requirements found in imported file');
        return;
      }

      // POST to existing save endpoint
      const response = await fetch(`/api/requirements/${environmentId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Class-Code': classContext.class_code
        },
        credentials: 'include',
        body: JSON.stringify({
          version: 'imported',
          requirements: importedRequirements
        })
      });

      if (response.ok) {
        setRequirements(importedRequirements);
        savedRequirementsRef.current = importedRequirements;
      }
    } catch (err) {
      console.warn('[ERDSnippet] Import failed:', err);
    }
  };

  // Mock resizing object for RawEditor
  const mockResizing = {
    handleVerticalResizeStart: () => {},
    handleHorizontalResizeStart: () => {}
  };

  // Read-only mode: show only the notation and visualization, no editing
  if (readOnly) {
    return (
      <div className="my-4 border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">ERD Diagram</span>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Read Only</span>
        </div>
        {initialContent && initialContent.trim() && (
          <div className="border-b border-gray-200 p-4 bg-gray-50">
            <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap">{initialContent}</pre>
          </div>
        )}
        {activeErdState.svgContent && (
          <div className="bg-gray-50 p-4" style={{ minHeight: '300px' }}>
            <ERDVisualization
              svgContent={activeErdState.svgContent}
              erdNotation={activeErdState.inputText}
              username={username || classContext?.username || ''}
              environment={environmentId || 'document'}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="my-4 border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">

      {/* Header: title + controls (no tab row here) */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">ERD Diagram</span>
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Interactive</span>
          {isStudent && !isSavable && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
              View Only (changes not saved)
            </span>
          )}
          {isStudent && isSavable && (
            <span className={`text-xs px-2 py-0.5 rounded ${
              erdSaveStatus === 'saved' ? 'bg-green-100 text-green-700' :
              erdSaveStatus === 'saving' ? 'bg-blue-100 text-blue-700' :
              'bg-green-50 text-green-600'
            }`}>
              {erdSaveStatus === 'saving' ? 'Saving...' :
               erdSavedAt ? `✓ Saved · ${formatSavedAt(erdSavedAt)}` :
               'Your work is saved'}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {activeErdState.relationships && activeErdState.relationships.length > 0 && (
            <div className="flex items-center space-x-2">
              <label className="text-xs font-medium text-gray-700">Relationship:</label>
              <select
                value={activeErdState.selectedRelationship || 'All'}
                onChange={(e) => activeErdState.setSelectedRelationship && activeErdState.setSelectedRelationship(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="All">All</option>
                {activeErdState.relationships.map((rel, index) => (
                  <option key={index} value={rel.name}>{rel.name}</option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={handleGenerate}
            disabled={activeErdState.isGenerating}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              activeErdState.isGenerating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {activeErdState.isGenerating ? 'Generating...' : 'Generate ERD'}
          </button>
        </div>
      </div>

      {/* Diagram — always visible, above the editor */}
      <div className="bg-gray-50 border-b border-gray-200 p-4" style={{ minHeight: '300px' }}>
        <ERDVisualization
          svgContent={activeErdState.svgContent}
          erdNotation={activeErdState.inputText}
          username={username || classContext?.username || ''}
          environment={environmentId || 'document'}
        />
      </div>

      {/* Edit Diagram / Clear Diagram buttons — shown when isEditable or isSavable */}
      {(isEditable || isSavable) && (
        <div className="border-t border-gray-200 px-4 py-2 flex justify-end items-center space-x-2 bg-white">
          {/* Undo button */}
          <button
            onClick={handleUndo}
            disabled={historyRef.current.index <= 0}
            title="Undo"
            className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              historyRef.current.index <= 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            <span>Undo</span>
          </button>
          {/* Redo button */}
          <button
            onClick={handleRedo}
            disabled={historyRef.current.index >= historyRef.current.items.length - 1}
            title="Redo"
            className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              historyRef.current.index >= historyRef.current.items.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
            </svg>
            <span>Redo</span>
          </button>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors bg-red-100 text-red-700 hover:bg-red-200"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear Diagram</span>
          </button>
          <button
            onClick={() => setEditorOpen(o => !o)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              editorOpen
                ? 'bg-green-700 text-white hover:bg-green-800'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>{editorOpen ? 'Hide Editor' : 'Edit Diagram'}</span>
          </button>
        </div>
      )}

      {/* Editor panel: only rendered when editable/savable and toggled open */}
      {(isEditable || isSavable) && editorOpen && (
        <div className="bg-white border-t border-gray-200">
          {/* Tab row — students only see Visual Editor */}
          <div className="flex border-b border-gray-200">
            {!isStudent && (
              <button
                onClick={() => setViewMode('raw')}
                className={`px-4 py-2 font-medium text-sm ${viewMode === 'raw'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Raw Notation
              </button>
            )}
            <button
              onClick={() => setViewMode('visual')}
              className={`px-4 py-2 font-medium text-sm ${viewMode === 'visual'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Visual Editor
            </button>
          </div>

          {/* Tab content */}
          {viewMode === 'visual' ? (
            <div className="p-4">
              <VisualEditor
                erdState={activeErdState}
                onSaveEvent={handleVisualEditorSaveEvent}
              />
            </div>
          ) : (
            <div className="p-4" style={{ height: '400px', overflowY: 'auto' }}>
              <RawEditor
                erdState={activeErdState}
                resizing={mockResizing}
                onSaveEvent={handleVisualEditorSaveEvent}
              />
            </div>
          )}
        </div>
      )}

      {/* Clear ERD confirmation dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Clear ERD notation?</h3>
            <p className="text-xs text-gray-600 mb-5">
              This will erase all notation and the diagram. Any parse errors or leftover content will be removed. This cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearConfirmed}
                className="px-4 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer with info */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-600">
          {isStudent && isSavable
            ? '💾 Your work is automatically saved. Pick up right where you left off next time.'
            : isStudent
              ? '💡 Your ERD interactions are tracked for analytics. Changes won\'t be saved to the document.'
              : '💾 Use the main document Save button to persist your ERD changes.'}
        </p>
        {onRequestFeedback && (
          <button
            onClick={() => onRequestFeedback(environmentId, docRequirements)}
            className="flex-shrink-0 flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.9 3.9 0 01-1.035 2.584L14 21H10l-.637-1.07a3.9 3.9 0 01-1.035-2.584l-.347-.347z" />
            </svg>
            <span>Get AI Feedback</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ERDSnippet;
